import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '@/services/api.js';
import { toast } from 'sonner';

/* ------------------------------------------------- */
/* WISHLIST TYPES                                    */
/* ------------------------------------------------- */
export type WishlistItem = {
  wishlistId: number;
  productId: number;
  productName: string;
  productDescription?: string;
  color?: string;
  rating?: number;
  imageUrl?: string;
  minPrice: number;
  maxPrice?: number;
  inStock?: boolean;
  addedDate: string;
  brand?: string;
};

/* ------------------------------------------------- */
/* CONTEXT TYPE                                      */
/* ------------------------------------------------- */
type WishlistContextType = {
  wishlist: WishlistItem[];
  wishlistCount: number;
  loading: boolean;
  isInWishlist: (productId: number) => boolean;
  fetchWishlist: () => Promise<void>;
  addToWishlist: (productId: number) => Promise<boolean>;
  removeFromWishlist: (productId: number) => Promise<boolean>;
  toggleWishlist: (productId: number) => Promise<boolean>;
  clearWishlist: () => Promise<boolean>;
  getWishlistCountForProduct: (productId: number) => Promise<number>;
};

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

/* ------------------------------------------------- */
/* WISHLIST PROVIDER                                 */
/* ------------------------------------------------- */
export const WishlistProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const wishlistCount = wishlist.length;

  const isInWishlist = (productId: number): boolean => {
    return wishlist.some(item => item.productId === productId);
  };

  // ──────── FETCH WISHLIST (WITH TRANSFORMATION) ────────
  const fetchWishlist = async () => {
    if (!user.isAuthenticated) {
      setWishlist([]);
      return;
    }

    try {
      setLoading(true);
      console.log('WishlistContext: Fetching user wishlist');

      const response = await authFetch('/api/wishlist');
      if (response.ok) {
        const rawData = await response.json();
        console.log('WishlistContext: Raw wishlist data:', rawData);

        // Transform backend offerPrice/originalPrice → minPrice/maxPrice
        const transformedData = Array.isArray(rawData)
          ? rawData.map((item: any) => ({
              ...item,
              minPrice: item.offerPrice ?? item.originalPrice ?? 0,
              maxPrice: item.originalPrice ?? null,
            }))
          : [];

        setWishlist(transformedData);
      } else {
        console.error('WishlistContext: Failed to fetch wishlist:', response.status);
        setWishlist([]);
      }
    } catch (error) {
      console.error('WishlistContext: Error fetching wishlist:', error);
      setWishlist([]);
    } finally {
      setLoading(false);
    }
  };

  // ──────── ADD TO WISHLIST (WITH TRANSFORMATION) ────────
  const addToWishlist = async (productId: number): Promise<boolean> => {
    if (!user.isAuthenticated) {
      console.log('WishlistContext: Authentication required to add to wishlist');
      toast.error('Please log in to add items to wishlist');
      return false;
    }

    if (isInWishlist(productId)) {
      console.log('WishlistContext: Product already in wishlist:', productId);
      return false;
    }

    try {
      console.log('WishlistContext: Adding product to wishlist:', productId);

      const response = await authFetch(`/api/wishlist/product/${productId}`, {
        method: 'POST',
      });

      if (response.ok) {
        const rawItem = await response.json();
        console.log('WishlistContext: Product added to wishlist:', rawItem);

        // Transform the new item
        const newItem: WishlistItem = {
          ...rawItem,
          minPrice: rawItem.offerPrice ?? rawItem.originalPrice ?? 0,
          maxPrice: rawItem.originalPrice ?? null,
        };

        setWishlist(prev => [...prev, newItem]);
        toast.success('Added to wishlist');
        return true;
      } else {
        const errorText = await response.text();
        console.error('WishlistContext: Failed to add to wishlist:', response.status, errorText);
        toast.error('Failed to add to wishlist');
        return false;
      }
    } catch (error) {
      console.error('WishlistContext: Error adding to wishlist:', error);
      toast.error('Error adding to wishlist');
      return false;
    }
  };

  // ──────── REMOVE FROM WISHLIST ────────
  const removeFromWishlist = async (productId: number): Promise<boolean> => {
    if (!user.isAuthenticated) {
      console.log('WishlistContext: Authentication required to remove from wishlist');
      return false;
    }

    if (!isInWishlist(productId)) {
      console.log('WishlistContext: Product not in wishlist:', productId);
      return false;
    }

    try {
      console.log('WishlistContext: Removing product from wishlist:', productId);

      const response = await authFetch(`/api/wishlist/remove/${productId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('WishlistContext: Product removed from wishlist:', productId);
        setWishlist(prev => prev.filter(item => item.productId !== productId));
        toast.success('Removed from wishlist');
        return true;
      } else {
        console.error('WishlistContext: Failed to remove from wishlist:', response.status);
        toast.error('Failed to remove from wishlist');
        return false;
      }
    } catch (error) {
      console.error('WishlistContext: Error removing from wishlist:', error);
      toast.error('Error removing from wishlist');
      return false;
    }
  };

  const toggleWishlist = async (productId: number): Promise<boolean> => {
    if (isInWishlist(productId)) {
      return await removeFromWishlist(productId);
    } else {
      return await addToWishlist(productId);
    }
  };

  const clearWishlist = async (): Promise<boolean> => {
    if (!user.isAuthenticated) {
      return false;
    }

    try {
      console.log('WishlistContext: Clearing wishlist');

      const response = await authFetch('/api/wishlist/clear', {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('WishlistContext: Wishlist cleared successfully');
        setWishlist([]);
        toast.success('Wishlist cleared');
        return true;
      } else {
        console.error('WishlistContext: Failed to clear wishlist:', response.status);
        toast.error('Failed to clear wishlist');
        return false;
      }
    } catch (error) {
      console.error('WishlistContext: Error clearing wishlist:', error);
      toast.error('Error clearing wishlist');
      return false;
    }
  };

  const getWishlistCountForProduct = async (productId: number): Promise<number> => {
    try {
      console.log('WishlistContext: Getting wishlist count for product:', productId);
      return wishlistCount;
    } catch (error) {
      console.error('WishlistContext: Error getting wishlist count:', error);
      return 0;
    }
  };

  useEffect(() => {
    if (user.isAuthenticated) {
      fetchWishlist();
    } else {
      setWishlist([]);
    }
  }, [user.isAuthenticated]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        wishlistCount,
        loading,
        isInWishlist,
        fetchWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        clearWishlist,
        getWishlistCountForProduct,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = (): WishlistContextType => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return ctx;
};
